import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Heart, ChatCircle, User, Clock, MagnifyingGlass, ArrowLeft } from '@phosphor-icons/react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForumSection = ({ onAuthRequired }) => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewPostForm, setShowNewPostForm] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [newPost, setNewPost] = useState({ title: '', content: '', category: 'general' });
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const categories = [
    { id: 'all', name: 'All Posts' },
    { id: 'general', name: 'General' },
    { id: 'anxiety', name: 'Anxiety & Stress' },
    { id: 'depression', name: 'Depression' },
    { id: 'relationships', name: 'Relationships' },
    { id: 'self-care', name: 'Self Care' },
    { id: 'success', name: 'Success Stories' }
  ];

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/forum/posts`, {
        params: { category: activeCategory !== 'all' ? activeCategory : undefined }
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [activeCategory]);

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!user) {
      onAuthRequired();
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/forum/posts`, newPost, { withCredentials: true });
      setNewPost({ title: '', content: '', category: 'general' });
      setShowNewPostForm(false);
      fetchPosts();
    } catch (error) {
      console.error('Failed to create post:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLikePost = async (postId) => {
    if (!user) {
      onAuthRequired();
      return;
    }

    try {
      await axios.post(`${API}/forum/posts/${postId}/like`, {}, { withCredentials: true });
      fetchPosts();
    } catch (error) {
      console.error('Failed to like post:', error);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!user) {
      onAuthRequired();
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/forum/posts/${selectedPost.id}/comments`, 
        { content: newComment }, 
        { withCredentials: true }
      );
      setNewComment('');
      // Refresh the selected post
      const response = await axios.get(`${API}/forum/posts/${selectedPost.id}`);
      setSelectedPost(response.data);
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const viewPost = async (postId) => {
    try {
      const response = await axios.get(`${API}/forum/posts/${postId}`);
      setSelectedPost(response.data);
    } catch (error) {
      console.error('Failed to fetch post:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <section id="community" className="py-24 sm:py-32 bg-[#F2EBE1]" data-testid="forum-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-['Outfit'] font-medium text-[#2A2825] mb-4">
            Community Forum
          </h2>
          <p className="text-lg text-[#5C564F] max-w-2xl mx-auto">
            Connect with others who understand. Share your experiences, find support, and know you're not alone.
          </p>
        </motion.div>

        {/* Selected Post View */}
        {selectedPost ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-3xl p-8 border border-[#E5DCD0]"
            data-testid="post-detail"
          >
            <button
              onClick={() => setSelectedPost(null)}
              className="flex items-center gap-2 text-[#5C564F] hover:text-[#2A2825] mb-6"
              data-testid="back-to-posts"
            >
              <ArrowLeft size={20} /> Back to posts
            </button>

            <div className="mb-6">
              <span className="text-xs font-medium text-[#647B64] uppercase tracking-wider bg-[#647B64]/10 px-3 py-1 rounded-full">
                {selectedPost.category}
              </span>
            </div>

            <h2 className="text-2xl font-['Outfit'] font-semibold text-[#2A2825] mb-4">
              {selectedPost.title}
            </h2>

            <div className="flex items-center gap-4 text-sm text-[#5C564F] mb-6">
              <span className="flex items-center gap-1">
                <User size={16} /> {selectedPost.author_name}
              </span>
              <span className="flex items-center gap-1">
                <Clock size={16} /> {formatDate(selectedPost.created_at)}
              </span>
            </div>

            <p className="text-[#5C564F] leading-relaxed whitespace-pre-wrap mb-8">
              {selectedPost.content}
            </p>

            <div className="flex items-center gap-4 pb-8 border-b border-[#E5DCD0]">
              <button
                onClick={() => handleLikePost(selectedPost.id)}
                className="flex items-center gap-2 text-[#5C564F] hover:text-[#B46C4A] transition-colors"
                data-testid="like-post-btn"
              >
                <Heart size={20} weight="fill" className="text-[#B46C4A]" />
                {selectedPost.likes} likes
              </button>
              <span className="flex items-center gap-2 text-[#5C564F]">
                <ChatCircle size={20} />
                {selectedPost.comments?.length || 0} comments
              </span>
            </div>

            {/* Comments */}
            <div className="mt-8">
              <h3 className="text-lg font-['Outfit'] font-semibold text-[#2A2825] mb-4">
                Comments ({selectedPost.comments?.length || 0})
              </h3>

              <form onSubmit={handleAddComment} className="mb-6" data-testid="comment-form">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={user ? "Share your thoughts..." : "Sign in to comment"}
                  rows={3}
                  className="w-full bg-[#F9F6F0] border border-[#E5DCD0] rounded-2xl px-4 py-3 resize-none mb-3"
                  data-testid="comment-input"
                  disabled={!user}
                />
                <button
                  type="submit"
                  disabled={!newComment.trim() || submitting || !user}
                  className="bg-[#B46C4A] hover:bg-[#9B5C3E] disabled:bg-[#E5DCD0] text-white px-6 py-2 rounded-full text-sm font-medium transition-colors"
                  data-testid="submit-comment-btn"
                >
                  {submitting ? 'Posting...' : 'Post Comment'}
                </button>
              </form>

              <div className="space-y-4">
                {selectedPost.comments?.map((comment, index) => (
                  <div key={comment.id || index} className="bg-[#F9F6F0] rounded-2xl p-4" data-testid={`comment-${index}`}>
                    <div className="flex items-center gap-2 text-sm text-[#5C564F] mb-2">
                      <div className="w-8 h-8 bg-[#647B64] rounded-full flex items-center justify-center text-white">
                        <User size={14} weight="fill" />
                      </div>
                      <span className="font-medium text-[#2A2825]">{comment.author_name}</span>
                      <span>•</span>
                      <span>{formatDate(comment.created_at)}</span>
                    </div>
                    <p className="text-[#5C564F] pl-10">{comment.content}</p>
                  </div>
                ))}

                {(!selectedPost.comments || selectedPost.comments.length === 0) && (
                  <p className="text-center text-[#5C564F] py-4">No comments yet. Be the first to share your thoughts!</p>
                )}
              </div>
            </div>
          </motion.div>
        ) : (
          <>
            {/* Actions Bar */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                      activeCategory === category.id
                        ? 'bg-[#B46C4A] text-white'
                        : 'bg-white text-[#5C564F] hover:bg-[#E5DCD0] border border-[#E5DCD0]'
                    }`}
                    data-testid={`forum-category-${category.id}`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>

              <button
                onClick={() => user ? setShowNewPostForm(true) : onAuthRequired()}
                className="bg-[#B46C4A] hover:bg-[#9B5C3E] text-white px-6 py-3 rounded-full font-medium flex items-center gap-2 transition-all duration-300"
                data-testid="new-post-btn"
              >
                <Plus size={20} weight="bold" /> New Post
              </button>
            </div>

            {/* New Post Form Modal */}
            {showNewPostForm && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                onClick={() => setShowNewPostForm(false)}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white rounded-3xl p-8 w-full max-w-lg"
                  onClick={(e) => e.stopPropagation()}
                  data-testid="new-post-modal"
                >
                  <h3 className="text-2xl font-['Outfit'] font-semibold text-[#2A2825] mb-6">Share with the Community</h3>
                  
                  <form onSubmit={handleCreatePost} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#5C564F] mb-2">Title</label>
                      <input
                        type="text"
                        value={newPost.title}
                        onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                        placeholder="Give your post a title"
                        className="w-full bg-[#F9F6F0] border border-[#E5DCD0] rounded-2xl px-4 py-3"
                        data-testid="post-title-input"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#5C564F] mb-2">Category</label>
                      <select
                        value={newPost.category}
                        onChange={(e) => setNewPost({ ...newPost, category: e.target.value })}
                        className="w-full bg-[#F9F6F0] border border-[#E5DCD0] rounded-2xl px-4 py-3"
                        data-testid="post-category-select"
                      >
                        {categories.filter(c => c.id !== 'all').map((category) => (
                          <option key={category.id} value={category.id}>{category.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#5C564F] mb-2">Your Story</label>
                      <textarea
                        value={newPost.content}
                        onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                        placeholder="Share what's on your mind. This is a safe space..."
                        rows={6}
                        className="w-full bg-[#F9F6F0] border border-[#E5DCD0] rounded-2xl px-4 py-3 resize-none"
                        data-testid="post-content-input"
                        required
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowNewPostForm(false)}
                        className="flex-1 bg-[#F2EBE1] text-[#5C564F] py-3 rounded-full font-medium hover:bg-[#E5DCD0] transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="flex-1 bg-[#B46C4A] hover:bg-[#9B5C3E] text-white py-3 rounded-full font-medium transition-colors disabled:bg-[#E5DCD0]"
                        data-testid="submit-post-btn"
                      >
                        {submitting ? 'Posting...' : 'Share Post'}
                      </button>
                    </div>
                  </form>
                </motion.div>
              </motion.div>
            )}

            {/* Posts List */}
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="loader"></div>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6" data-testid="posts-grid">
                {posts.map((post, index) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white rounded-3xl p-6 border border-[#E5DCD0] hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                    onClick={() => viewPost(post.id)}
                    data-testid={`forum-post-${index}`}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-[#647B64] rounded-full flex items-center justify-center text-white">
                        <User size={18} weight="fill" />
                      </div>
                      <div>
                        <p className="font-medium text-[#2A2825]">{post.author_name}</p>
                        <p className="text-xs text-[#5C564F]">{formatDate(post.created_at)}</p>
                      </div>
                      <span className="ml-auto text-xs font-medium text-[#647B64] bg-[#647B64]/10 px-3 py-1 rounded-full">
                        {post.category}
                      </span>
                    </div>

                    <h3 className="text-lg font-['Outfit'] font-semibold text-[#2A2825] mb-2 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-[#5C564F] text-sm line-clamp-3 mb-4">
                      {post.content}
                    </p>

                    <div className="flex items-center gap-4 text-sm text-[#5C564F]">
                      <span className="flex items-center gap-1">
                        <Heart size={16} weight="fill" className="text-[#B46C4A]" /> {post.likes}
                      </span>
                      <span className="flex items-center gap-1">
                        <ChatCircle size={16} /> {post.comments_count}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {posts.length === 0 && !loading && (
              <div className="text-center py-12">
                <p className="text-[#5C564F] mb-4">No posts yet in this category.</p>
                <button
                  onClick={() => user ? setShowNewPostForm(true) : onAuthRequired()}
                  className="text-[#B46C4A] hover:text-[#9B5C3E] font-medium"
                >
                  Be the first to share your story
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default ForumSection;
